// ═══════════════════════════════════════════════════════════════════════════
// MFASection — Configuración de Multi-Factor Authentication (TOTP)
// ═══════════════════════════════════════════════════════════════════════════
//
// SEC-F0.3 (mayo 2026) — MFA opcional. El usuario puede enrollar su segundo
// factor desde Mi Cuenta. El MFA forzado al staff (por flag o rol, no por el
// mail: `functions/auth/mfaPolicy.js`) es SEC-B3 (Fase Bronze).
//
// Flujo de enroll TOTP:
//   1) Reautenticar (Firebase exige login reciente para mutar MFA).
//   2) Generar TOTP secret → mostrar QR + secret manual.
//   3) Usuario escanea con Authenticator (Google/Authy/Microsoft/1Password).
//   4) Usuario ingresa código de 6 dígitos vigente.
//   5) Confirmar nombre del factor y persistir.
//
// Flujo de unenroll: pedir confirmación → llamar `multiFactor(user).unenroll(factor)`.
//
// Notas:
// - Firebase emite `auth/requires-recent-login` si la sesión es vieja. Manejamos
//   reauth inline (Google popup o password prompt según provider).
// - El secret TOTP es el mismo que se usa para verificar luego en login. Si el
//   usuario pierde el celular, debe contactar a un admin para limpiar el factor.
// ═══════════════════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react'
import {
  multiFactor,
  TotpMultiFactorGenerator,
  EmailAuthProvider,
  GoogleAuthProvider,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
} from 'firebase/auth'
import { auth } from '@core/lib/firebase'

const IconShield = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  </svg>
)

const IconCheck = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className="w-4 h-4">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
)

const IconCopy = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
  </svg>
)

// Renderiza un QR code simple usando un servicio público vía SVG. Para no
// agregar dependencias, usamos `api.qrserver.com`. Si querés evitar terceros
// podés cambiarlo por `qrcode-svg` instalado localmente.
function QRImage({ otpauthUrl }) {
  const src = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&margin=8&data=${encodeURIComponent(otpauthUrl)}`
  return (
    <img
      src={src}
      alt="Código QR para configurar tu app de autenticación"
      className="rounded-lg bg-white p-2"
      width={200}
      height={200}
    />
  )
}

/**
 * @param {object} props
 * @param {boolean} [props.forced=false] — SEC-B3: cuando es true, MFASection
 *   actúa como gate obligatorio:
 *     - Muestra header explicativo con motivo de la política.
 *     - Oculta botón "Quitar" del factor enrollado (no se puede desactivar).
 *     - Marca el componente con estilo "warning" (border ámbar).
 *   En modo no-forced (default), MFA es opcional y el usuario puede activar/
 *   desactivar libremente desde Mi Cuenta.
 * @param {Function} [props.onEnrolled] — callback opcional que se llama
 *   cuando el usuario termina el enrollment. Usado por MFAGate para refrescar
 *   el token y dejar pasar al usuario.
 */
export default function MFASection({ forced = false, onEnrolled = null } = {}) {
  const user = auth.currentUser
  const [enrolledFactors, setEnrolledFactors] = useState([])
  const [step, setStep] = useState('idle') // idle | reauth | qr | verify | naming | done
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  // Estado del wizard
  const [password, setPassword] = useState('')
  const [totpSecret, setTotpSecret] = useState(null)
  const [otpauthUrl, setOtpauthUrl] = useState('')
  const [code, setCode] = useState('')
  const [factorName, setFactorName] = useState('Mi celular')
  const [pendingAssertion, setPendingAssertion] = useState(null)
  const [secretCopied, setSecretCopied] = useState(false)

  // Refrescar lista de factores enrollados
  useEffect(() => {
    if (!user) return
    try {
      const mf = multiFactor(user)
      setEnrolledFactors(mf.enrolledFactors || [])
    } catch (e) {
      // multiFactor no disponible aún
      setEnrolledFactors([])
    }
  }, [user, step])

  function reset() {
    setStep('idle')
    setError('')
    setPassword('')
    setTotpSecret(null)
    setOtpauthUrl('')
    setCode('')
    setFactorName('Mi celular')
    setPendingAssertion(null)
    setSecretCopied(false)
  }

  // Detectar si el usuario es Google o email/password
  const providerId = user?.providerData?.[0]?.providerId || 'password'
  const esGoogle = providerId === 'google.com'

  async function handleStartEnroll() {
    setError('')
    if (esGoogle) {
      // Google: reauth con popup directamente y arrancamos enroll
      await doReauthAndStart()
    } else {
      // Email+pass: pedir password
      setStep('reauth')
    }
  }

  async function doReauthAndStart() {
    if (!user) return
    setBusy(true)
    setError('')
    try {
      if (esGoogle) {
        const provider = new GoogleAuthProvider()
        provider.setCustomParameters({ login_hint: user.email || '' })
        await reauthenticateWithPopup(user, provider)
      } else {
        if (!password) {
          setError('Ingresá tu contraseña actual')
          setBusy(false)
          return
        }
        const cred = EmailAuthProvider.credential(user.email, password)
        await reauthenticateWithCredential(user, cred)
      }

      // Generar TOTP secret
      const session = await multiFactor(user).getSession()
      const secret = await TotpMultiFactorGenerator.generateSecret(session)
      const url = secret.generateQrCodeUrl(user.email || 'usuario', 'Milbrands CoWork')
      setTotpSecret(secret)
      setOtpauthUrl(url)
      setStep('qr')
    } catch (e) {
      console.error('[MFA] reauth/secret', e)
      const map = {
        'auth/wrong-password': 'Contraseña incorrecta',
        'auth/popup-closed-by-user': 'Cerraste la ventana antes de confirmar',
        'auth/popup-blocked': 'El popup fue bloqueado por el navegador',
      }
      setError(map[e.code] || e.message || 'No se pudo verificar tu identidad')
    } finally {
      setBusy(false)
    }
  }

  async function handleVerifyCode() {
    if (!totpSecret) return
    setError('')
    if (!/^\d{6}$/.test(code.trim())) {
      setError('Ingresá los 6 dígitos del código')
      return
    }
    setBusy(true)
    try {
      const assertion = TotpMultiFactorGenerator.assertionForEnrollment(
        totpSecret,
        code.trim()
      )
      setPendingAssertion(assertion)
      setStep('naming')
    } catch (e) {
      console.error('[MFA] verify code', e)
      const map = {
        'auth/invalid-verification-code': 'Código incorrecto. Probá con el código actual de tu app.',
      }
      setError(map[e.code] || 'Código inválido. Asegurate de que el reloj de tu celular esté sincronizado.')
    } finally {
      setBusy(false)
    }
  }

  async function handleConfirmEnroll() {
    if (!pendingAssertion || !user) return
    setBusy(true)
    setError('')
    try {
      const name = (factorName || 'Mi celular').slice(0, 60)
      await multiFactor(user).enroll(pendingAssertion, name)
      setStep('done')
      // SEC-B3 forced mode: notificar al gate para que refresque el token y
      // deje pasar al usuario. El token nuevo va a tener el factor enrollado
      // y, cuando el usuario haga sign-in la próxima vez con su segundo factor,
      // el claim `firebase.sign_in_second_factor` va a aparecer.
      if (typeof onEnrolled === 'function') {
        try { onEnrolled() } catch (cbErr) { console.warn('[MFA] onEnrolled callback', cbErr) }
      }
    } catch (e) {
      console.error('[MFA] enroll', e)
      setError(e.message || 'No se pudo activar el segundo factor')
    } finally {
      setBusy(false)
    }
  }

  async function handleUnenroll(factor) {
    if (!user) return
    if (!confirm(`¿Quitar "${factor.displayName || 'este factor'}" como segundo factor? Si lo hacés, vas a entrar solo con email/Google.`)) return
    setBusy(true)
    setError('')
    try {
      await multiFactor(user).unenroll(factor)
      // refresh
      setEnrolledFactors(multiFactor(user).enrolledFactors || [])
    } catch (e) {
      console.error('[MFA] unenroll', e)
      // Si pide reauth, lo hacemos inline
      if (e.code === 'auth/requires-recent-login') {
        setError('Por seguridad, cerrá sesión y volvé a entrar antes de quitar el factor.')
      } else {
        setError(e.message || 'No se pudo quitar el factor')
      }
    } finally {
      setBusy(false)
    }
  }

  function copySecret() {
    if (!totpSecret) return
    try {
      navigator.clipboard.writeText(totpSecret.secretKey)
      setSecretCopied(true)
      setTimeout(() => setSecretCopied(false), 2000)
    } catch {}
  }

  if (!user) return null

  const tieneFactor = enrolledFactors.length > 0

  return (
    <div className={`glass-card p-5 mt-5 ${forced ? 'ring-2 ring-amber-500/40 border-amber-500/30' : ''}`}>
      <div className="flex items-start gap-4">
        <div className={`w-12 h-12 rounded-2xl border flex items-center justify-center shrink-0 ${forced ? 'bg-amber-500/10 border-amber-500/40 text-amber-300' : 'bg-white/[0.04] border-white/[0.08] text-emerald-300'}`}>
          <IconShield />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-[16px] font-semibold text-white">
              {forced ? 'Activá 2FA para continuar' : 'Autenticación de dos factores (2FA)'}
            </h2>
            {tieneFactor && (
              <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-300 border border-emerald-500/30">
                <IconCheck /> Activa
              </span>
            )}
            {forced && !tieneFactor && (
              <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-300 border border-amber-500/30">
                Obligatorio
              </span>
            )}
          </div>
          {forced ? (
            <p className="text-[13px] text-zinc-300 mt-1 leading-relaxed">
              Como consultor de Milbrands tenés acceso a operaciones críticas
              (kill switches, paquetes, credenciales IA, exportación GDPR).
              Por política de seguridad, <strong>todas las cuentas del equipo de Milbrands
              necesitan un segundo factor activo</strong>. Te toma 2 minutos —
              instalá una app de códigos y escaneá el QR.
            </p>
          ) : (
            <p className="text-[13px] text-zinc-400 mt-1 leading-relaxed">
              Sumá un código de 6 dígitos al login. Aunque alguien tenga tu contraseña,
              no va a poder entrar sin tu celular. Usá <strong>Google Authenticator</strong>,
              <strong> Authy</strong>, <strong>Microsoft Authenticator</strong> o <strong>1Password</strong>.
            </p>
          )}

          {error && (
            <div className="mt-3 text-[12px] bg-rose-500/10 border border-rose-500/30 text-rose-200 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          {/* Estado: lista de factores enrollados */}
          {step === 'idle' && tieneFactor && (
            <div className="mt-4 space-y-2">
              {enrolledFactors.map(f => (
                <div key={f.uid} className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl bg-white/[0.02] border border-white/[0.06]">
                  <div className="min-w-0">
                    <div className="text-[13px] font-semibold text-white truncate">
                      {f.displayName || 'Segundo factor'}
                    </div>
                    <div className="text-[11px] text-zinc-500 truncate">
                      {f.factorId === 'totp' ? 'App de códigos (TOTP)' : f.factorId}
                      {f.enrollmentTime && ` · activado el ${new Date(f.enrollmentTime).toLocaleDateString('es-AR')}`}
                    </div>
                  </div>
                  {!forced && (
                    <button
                      onClick={() => handleUnenroll(f)}
                      disabled={busy}
                      className="text-[12px] font-semibold px-3 py-2 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-300 hover:bg-rose-500/20 disabled:opacity-40 shrink-0"
                    >
                      Quitar
                    </button>
                  )}
                  {forced && (
                    <span className="text-[11px] text-zinc-500 italic shrink-0">
                      Obligatorio por política Milbrands
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Estado: sin factores → CTA activar */}
          {step === 'idle' && !tieneFactor && (
            <div className="mt-4">
              <button
                onClick={handleStartEnroll}
                disabled={busy}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-500 text-zinc-900 font-semibold text-[13px] hover:bg-emerald-400 disabled:opacity-40"
              >
                <IconShield />
                Activar autenticación de dos factores
              </button>
              <p className="text-[11px] text-zinc-500 mt-2">
                Necesitás tu celular a mano y una app de códigos instalada.
              </p>
            </div>
          )}

          {/* Estado: pedir password (solo email/pass users) */}
          {step === 'reauth' && (
            <div className="mt-4 space-y-3">
              <div className="text-[13px] text-zinc-300">
                Para confirmar que sos vos, ingresá tu contraseña actual.
              </div>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Tu contraseña"
                className="w-full max-w-sm px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-[13px] text-white placeholder:text-zinc-600 focus:border-white/[0.2] outline-none"
                autoFocus
              />
              <div className="flex items-center gap-2">
                <button
                  onClick={doReauthAndStart}
                  disabled={busy || !password}
                  className="text-[12px] font-semibold px-3 py-2 rounded-lg bg-white text-zinc-900 hover:bg-zinc-100 disabled:opacity-40"
                >
                  {busy ? 'Verificando…' : 'Continuar'}
                </button>
                <button
                  onClick={reset}
                  disabled={busy}
                  className="text-[12px] font-semibold px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-zinc-300 hover:bg-white/[0.08] disabled:opacity-40"
                >
                  Cancelar
                </button>
              </div>
            </div>
          )}

          {/* Estado: mostrar QR */}
          {step === 'qr' && totpSecret && (
            <div className="mt-4 space-y-3">
              <div className="text-[13px] text-zinc-300 leading-relaxed">
                Abrí tu app de autenticación y escaneá este QR. Si no podés escanearlo,
                copiá el código de abajo y pegalo manualmente.
              </div>
              <div className="flex flex-col sm:flex-row items-start gap-4">
                <QRImage otpauthUrl={otpauthUrl} />
                <div className="flex-1 min-w-0 space-y-2">
                  <div className="text-[11px] text-zinc-500">Código manual:</div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <code className="px-3 py-2 rounded bg-black/30 border border-white/[0.08] text-[12px] font-mono text-zinc-200 break-all">
                      {totpSecret.secretKey}
                    </code>
                    <button
                      onClick={copySecret}
                      className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-zinc-300 hover:bg-white/[0.08]"
                    >
                      <IconCopy />
                      {secretCopied ? 'Copiado' : 'Copiar'}
                    </button>
                  </div>
                  <div className="text-[11px] text-zinc-500 pt-2">
                    Tipo: TOTP · 30s · 6 dígitos · SHA1
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 pt-2">
                <button
                  onClick={() => setStep('verify')}
                  className="text-[12px] font-semibold px-3 py-2 rounded-lg bg-white text-zinc-900 hover:bg-zinc-100"
                >
                  Ya escaneé, continuar
                </button>
                <button
                  onClick={reset}
                  className="text-[12px] font-semibold px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-zinc-300 hover:bg-white/[0.08]"
                >
                  Cancelar
                </button>
              </div>
            </div>
          )}

          {/* Estado: verificar código */}
          {step === 'verify' && (
            <div className="mt-4 space-y-3">
              <div className="text-[13px] text-zinc-300">
                Ingresá el código de 6 dígitos que muestra tu app ahora.
              </div>
              <input
                type="text"
                inputMode="numeric"
                pattern="\d*"
                maxLength={6}
                value={code}
                onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="123456"
                className="w-40 px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-[18px] tracking-[0.4em] font-mono text-white placeholder:text-zinc-600 focus:border-white/[0.2] outline-none"
                autoFocus
              />
              <div className="flex items-center gap-2">
                <button
                  onClick={handleVerifyCode}
                  disabled={busy || code.length !== 6}
                  className="text-[12px] font-semibold px-3 py-2 rounded-lg bg-white text-zinc-900 hover:bg-zinc-100 disabled:opacity-40"
                >
                  {busy ? 'Verificando…' : 'Verificar código'}
                </button>
                <button
                  onClick={() => setStep('qr')}
                  disabled={busy}
                  className="text-[12px] font-semibold px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-zinc-300 hover:bg-white/[0.08] disabled:opacity-40"
                >
                  Volver al QR
                </button>
              </div>
            </div>
          )}

          {/* Estado: nombrar factor y confirmar enroll */}
          {step === 'naming' && (
            <div className="mt-4 space-y-3">
              <div className="text-[13px] text-zinc-300">
                Ponele un nombre para reconocerlo (ej: "iPhone de trabajo", "Authenticator del laburo"). Solo lo verás vos.
              </div>
              <input
                type="text"
                value={factorName}
                onChange={e => setFactorName(e.target.value)}
                maxLength={60}
                className="w-full max-w-sm px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-[13px] text-white placeholder:text-zinc-600 focus:border-white/[0.2] outline-none"
              />
              <div className="flex items-center gap-2">
                <button
                  onClick={handleConfirmEnroll}
                  disabled={busy || !factorName.trim()}
                  className="text-[12px] font-semibold px-3 py-2 rounded-lg bg-emerald-500 text-zinc-900 font-semibold hover:bg-emerald-400 disabled:opacity-40"
                >
                  {busy ? 'Activando…' : 'Activar segundo factor'}
                </button>
                <button
                  onClick={reset}
                  disabled={busy}
                  className="text-[12px] font-semibold px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-zinc-300 hover:bg-white/[0.08] disabled:opacity-40"
                >
                  Cancelar
                </button>
              </div>
            </div>
          )}

          {/* Estado: éxito */}
          {step === 'done' && (
            <div className="mt-4 space-y-3">
              <div className="px-3 py-2.5 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-200 text-[13px]">
                <div className="font-semibold flex items-center gap-2"><IconCheck /> Listo, 2FA activado</div>
                <div className="text-[11px] text-emerald-300/80 mt-1">
                  La próxima vez que entres, te vamos a pedir el código de tu app después de la contraseña.
                </div>
              </div>
              <button
                onClick={reset}
                className="text-[12px] font-semibold px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-zinc-300 hover:bg-white/[0.08]"
              >
                Volver
              </button>
            </div>
          )}

          {/* Footer info */}
          <div className="mt-4 text-[11px] text-zinc-500 space-y-1">
            <div>• Si perdés tu celular, contactá a un admin para que limpie tu segundo factor.</div>
            <div>• El código se basa en la hora del celular — si no coincide, sincronizá la hora.</div>
          </div>
        </div>
      </div>
    </div>
  )
}
